"use client"

import * as React from "react"
import { format } from "date-fns/format"
import { isSameDay } from "date-fns/isSameDay"
import { ChevronDown } from "lucide-react"
import type { DateRange as DayPickerDateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import {
  CalendarDaysIcon,
  type CalendarDaysIconHandle,
} from "./calendar-days-icon"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import type { ComparisonPreset, DateRange } from "@/types/trade"
import {
  COMPARISON_PRESET_OPTIONS,
  computeComparisonRange,
} from "@/utils/comparisonDateRange"
import {
  DASHBOARD_DATE_PRESET_OPTIONS,
  dashboardDatePresetOptionsForViewport,
  getActiveDashboardDatePresetId,
  presetToLocalDateRange,
  type DashboardDatePresetTimeframe,
  type DashboardDateRangeChangeOptions,
} from "@/utils/dashboardDateWindowStorage"
import { endOfLocalDay, startOfLocalDay } from "@/utils/calendarWeekRanges"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

const MIN_CALENDAR_DATE = new Date("1900-01-01")

const DEFAULT_COMPARISON_PRESET: ComparisonPreset = "previous-period-days"

const PRIMARY_CALENDAR_CLASS_NAMES = {
  range_start:
    "relative isolate z-0 rounded-l-(--cell-radius) bg-[color:var(--brand-tropical-surf)]/15 after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-[color:var(--brand-tropical-surf)]/15",
  range_middle: "rounded-none bg-[color:var(--brand-tropical-surf)]/12",
  range_end:
    "relative isolate z-0 rounded-r-(--cell-radius) bg-[color:var(--brand-tropical-surf)]/15 after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-[color:var(--brand-tropical-surf)]/15",
  today:
    "rounded-(--cell-radius) bg-muted text-muted-foreground data-[selected=true]:rounded-none",
} as const

const SINGLE_CALENDAR_CLASS_NAMES = {
  today: PRIMARY_CALENDAR_CLASS_NAMES.today,
} as const

const COMPARISON_CALENDAR_CLASS_NAMES = {
  range_start:
    "relative isolate z-0 rounded-l-(--cell-radius) bg-[color:var(--brand-comparison-period)]/35 after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-[color:var(--brand-comparison-period)]/35",
  range_middle:
    "rounded-none bg-[color:var(--brand-comparison-period)]/22",
  range_end:
    "relative isolate z-0 rounded-r-(--cell-radius) bg-[color:var(--brand-comparison-period)]/35 after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-[color:var(--brand-comparison-period)]/35",
  today:
    "rounded-(--cell-radius) bg-muted text-muted-foreground data-[selected=true]:rounded-none",
} as const

export interface DateRangePickerProps {
  dateRange: DateRange
  onDateRangeChange: (
    dateRange: DateRange,
    options?: DashboardDateRangeChangeOptions
  ) => void
  /** Persisted / parent-selected preset — keeps highlight when dates match multiple presets. */
  selectedPresetId?: DashboardDatePresetTimeframe | null
  className?: string
  /**
   * `chrome` — header canvas fill (dashboard / trade-log toolbars).
   * `field` — transparent like form selects (generate-report wizard).
   */
  triggerSurface?: "chrome" | "field"
  /** Shown when no preset matches and range is empty. */
  emptyLabel?: string
  /** Range calendar columns — default `1` on mobile, `2` on desktop (override with `1` or `2`). */
  numberOfMonths?: 1 | 2
  /** Statistics comparison mode — requires all four comparison props. */
  comparisonEnabled?: boolean
  onComparisonEnabledChange?: (enabled: boolean) => void
  comparisonRange?: DateRange | null
  onComparisonRangeChange?: (range: DateRange | null) => void
  /** Earliest selectable day (default: no limit). */
  minDate?: Date | null
  /** The quick-select preset list under the calendar (default: shown). */
  showPresets?: boolean
}

function toDayPickerRange(dateRange: DateRange): DayPickerDateRange | undefined {
  if (!dateRange.startDate) return undefined

  const from = startOfLocalDay(dateRange.startDate)
  if (!dateRange.endDate) {
    return { from }
  }

  return {
    from,
    to: startOfLocalDay(dateRange.endDate),
  }
}

function presetToAppDateRange(
  presetId: DashboardDatePresetTimeframe
): DateRange {
  if (presetId === "all-time") {
    return { startDate: null, endDate: null }
  }

  const next = presetToLocalDateRange(presetId)
  return next ?? { startDate: null, endDate: null }
}

function formatRangeSegment(dateRange: DateRange): string | null {
  if (dateRange.startDate && dateRange.endDate) {
    if (isSameDay(dateRange.startDate, dateRange.endDate)) {
      return format(dateRange.startDate, "MMM d, yyyy")
    }
    return `${format(dateRange.startDate, "MMM d, yyyy")} – ${format(dateRange.endDate, "MMM d, yyyy")}`
  }
  if (dateRange.startDate) {
    return `From ${format(dateRange.startDate, "MMM d, yyyy")}`
  }
  if (dateRange.endDate) {
    return `Until ${format(dateRange.endDate, "MMM d, yyyy")}`
  }
  return null
}

function formatTriggerLabel(
  dateRange: DateRange,
  emptyLabel: string,
  comparisonEnabled: boolean,
  comparisonRange: DateRange | null | undefined,
  preferredPresetId?: DashboardDatePresetTimeframe | null,
  matchPresets = true
): string {
  if (
    comparisonEnabled &&
    comparisonRange?.startDate &&
    comparisonRange?.endDate
  ) {
    const primary = formatRangeSegment(dateRange)
    const comparison = formatRangeSegment(comparisonRange)
    if (primary && comparison) {
      return `${primary} vs ${comparison}`
    }
  }

  const activePreset = matchPresets ? getActiveDashboardDatePresetId(dateRange, preferredPresetId) : null
  if (activePreset) {
    return (
      DASHBOARD_DATE_PRESET_OPTIONS.find((option) => option.id === activePreset)
        ?.label ?? emptyLabel
    )
  }

  if (!dateRange.startDate && !dateRange.endDate) {
    return emptyLabel
  }

  const segment = formatRangeSegment(dateRange)
  return segment ?? "Select date range"
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** Latest month in a range (or today) — shown as the right-hand panel when `numberOfMonths` is 2. */
function focusMonthForRange(
  range: DateRange | null | undefined,
  fallback = new Date()
): Date {
  const ref = range?.endDate ?? range?.startDate ?? fallback
  return monthStart(ref)
}

/**
 * `month` prop for DayPicker: with two months, the library renders `month` then the
 * next month — so use the month before the focus month to get [previous, current].
 */
function pickerMonthForFocus(focus: Date, numberOfMonths: 1 | 2): Date {
  const focusMonth = monthStart(focus)
  if (numberOfMonths === 1) {
    return focusMonth
  }
  const firstVisible = new Date(focusMonth)
  firstVisible.setMonth(firstVisible.getMonth() - 1)
  return monthStart(firstVisible)
}

function isFutureCalendarDay(date: Date): boolean {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return date > today
}

function hasCompleteRange(dateRange: DateRange | null | undefined): boolean {
  return Boolean(dateRange?.startDate && dateRange?.endDate)
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  selectedPresetId: selectedPresetIdProp = null,
  className,
  triggerSurface = "chrome",
  emptyLabel = "All time",
  numberOfMonths: numberOfMonthsProp,
  comparisonEnabled = false,
  onComparisonEnabledChange,
  comparisonRange = null,
  onComparisonRangeChange,
  minDate = null,
  showPresets = true,
}: DateRangePickerProps) {
  const isMobile = useIsMobile()
  const resolvedNumberOfMonths: 1 | 2 =
    numberOfMonthsProp ?? (isMobile ? 1 : 2)

  const supportsComparison = Boolean(
    onComparisonEnabledChange && onComparisonRangeChange
  )

  const [open, setOpen] = React.useState(false)
  const [explicitPresetId, setExplicitPresetId] =
    React.useState<DashboardDatePresetTimeframe | null>(null)
  const [comparisonPreset, setComparisonPreset] =
    React.useState<ComparisonPreset>(DEFAULT_COMPARISON_PRESET)

  const preferredPresetId = explicitPresetId ?? selectedPresetIdProp

  React.useEffect(() => {
    if (selectedPresetIdProp) {
      setExplicitPresetId(null)
    }
  }, [selectedPresetIdProp])

  const dayPickerRange = toDayPickerRange(dateRange)
  const comparisonDayPickerRange = toDayPickerRange(
    comparisonRange ?? { startDate: null, endDate: null }
  )

  const [currentMonth, setCurrentMonth] = React.useState<Date>(() =>
    pickerMonthForFocus(focusMonthForRange(dateRange), resolvedNumberOfMonths)
  )
  const [comparisonMonth, setComparisonMonth] = React.useState<Date>(() =>
    pickerMonthForFocus(
      focusMonthForRange(comparisonRange ?? dateRange),
      resolvedNumberOfMonths
    )
  )

  const triggerLabel = formatTriggerLabel(
    dateRange,
    emptyLabel,
    comparisonEnabled,
    comparisonRange,
    preferredPresetId,
    showPresets
  )
  const activePresetId = getActiveDashboardDatePresetId(dateRange, preferredPresetId)

  const visibleDatePresetOptions = React.useMemo(
    () => dashboardDatePresetOptionsForViewport(isMobile, activePresetId),
    [isMobile, activePresetId]
  )

  const iconRef = React.useRef<CalendarDaysIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  React.useEffect(() => {
    const ref = dateRange.endDate ?? dateRange.startDate
    if (ref) {
      setCurrentMonth(pickerMonthForFocus(ref, resolvedNumberOfMonths))
    }
  }, [dateRange.startDate, dateRange.endDate, resolvedNumberOfMonths])

  React.useEffect(() => {
    const ref = comparisonRange?.endDate ?? comparisonRange?.startDate
    if (ref) {
      setComparisonMonth(pickerMonthForFocus(ref, resolvedNumberOfMonths))
    }
  }, [
    comparisonRange?.startDate,
    comparisonRange?.endDate,
    resolvedNumberOfMonths,
  ])

  React.useEffect(() => {
    if (
      !supportsComparison ||
      !comparisonEnabled ||
      !onComparisonRangeChange ||
      comparisonPreset === "custom" ||
      !hasCompleteRange(dateRange)
    ) {
      return
    }
    const next = computeComparisonRange(dateRange, comparisonPreset)
    onComparisonRangeChange(next)
  }, [
    supportsComparison,
    comparisonEnabled,
    comparisonPreset,
    dateRange.startDate,
    dateRange.endDate,
    onComparisonRangeChange,
    dateRange,
  ])

  const handlePresetSelect = React.useCallback(
    (presetId: DashboardDatePresetTimeframe) => {
      const next = presetToAppDateRange(presetId)
      setExplicitPresetId(presetId)
      onDateRangeChange(next, { presetId })

      setCurrentMonth(
        pickerMonthForFocus(focusMonthForRange(next), resolvedNumberOfMonths)
      )

      if (supportsComparison && comparisonEnabled && presetId === "all-time") {
        onComparisonEnabledChange?.(false)
        onComparisonRangeChange?.(null)
      }

      setOpen(false)
    },
    [
      onDateRangeChange,
      supportsComparison,
      comparisonEnabled,
      onComparisonEnabledChange,
      onComparisonRangeChange,
      resolvedNumberOfMonths,
    ]
  )

  const handleComparisonPresetSelect = React.useCallback(
    (preset: ComparisonPreset) => {
      setComparisonPreset(preset)
      if (!hasCompleteRange(dateRange)) return
      const next = computeComparisonRange(dateRange, preset)
      onComparisonRangeChange?.(next)
      if (next) {
        setComparisonMonth(
          pickerMonthForFocus(focusMonthForRange(next), resolvedNumberOfMonths)
        )
      }
    },
    [dateRange, onComparisonRangeChange, resolvedNumberOfMonths]
  )

  const handleCalendarSelect = React.useCallback(
    (range: DayPickerDateRange | undefined) => {
      setExplicitPresetId(null)
      if (!range?.from) {
        onDateRangeChange({ startDate: null, endDate: null })
        if (supportsComparison && comparisonEnabled) {
          onComparisonEnabledChange?.(false)
          onComparisonRangeChange?.(null)
        }
        return
      }

      const startDate = startOfLocalDay(range.from)
      setCurrentMonth(pickerMonthForFocus(range.from, resolvedNumberOfMonths))

      if (!range.to) {
        onDateRangeChange({ startDate, endDate: null })
        return
      }

      onDateRangeChange({
        startDate,
        endDate: endOfLocalDay(range.to),
      })
    },
    [
      onDateRangeChange,
      supportsComparison,
      comparisonEnabled,
      onComparisonEnabledChange,
      onComparisonRangeChange,
      resolvedNumberOfMonths,
    ]
  )

  const handleComparisonCalendarSelect = React.useCallback(
    (range: DayPickerDateRange | undefined) => {
      if (!range?.from) {
        setComparisonPreset("custom")
        onComparisonRangeChange?.(null)
        return
      }

      const startDate = startOfLocalDay(range.from)
      setComparisonMonth(pickerMonthForFocus(range.from, resolvedNumberOfMonths))
      setComparisonPreset("custom")

      if (!range.to) {
        onComparisonRangeChange?.({ startDate, endDate: null })
        return
      }

      onComparisonRangeChange?.({
        startDate,
        endDate: endOfLocalDay(range.to),
      })
    },
    [onComparisonRangeChange, resolvedNumberOfMonths]
  )

  const handleComparisonToggle = React.useCallback(
    (checked: boolean) => {
      onComparisonEnabledChange?.(checked)
      if (!checked) {
        onComparisonRangeChange?.(null)
        setComparisonPreset(DEFAULT_COMPARISON_PRESET)
        return
      }
      setComparisonPreset(DEFAULT_COMPARISON_PRESET)
      if (hasCompleteRange(dateRange)) {
        onComparisonRangeChange?.(
          computeComparisonRange(dateRange, DEFAULT_COMPARISON_PRESET)
        )
      }
    },
    [onComparisonEnabledChange, onComparisonRangeChange, dateRange]
  )

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (nextOpen) {
        setCurrentMonth(
          pickerMonthForFocus(focusMonthForRange(dateRange), resolvedNumberOfMonths)
        )
        if (comparisonRange?.startDate || comparisonRange?.endDate) {
          setComparisonMonth(
            pickerMonthForFocus(
              focusMonthForRange(comparisonRange),
              resolvedNumberOfMonths
            )
          )
        }
      }
    },
    [
      dateRange.startDate,
      dateRange.endDate,
      comparisonRange?.startDate,
      comparisonRange?.endDate,
      resolvedNumberOfMonths,
    ]
  )

  const primaryRangeReady = hasCompleteRange(dateRange)

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-date-range-trigger=""
          data-date-range-surface={triggerSurface}
          className={cn(
            "h-8 shrink-0 justify-between gap-1.5 font-normal normal-case tracking-normal",
            comparisonEnabled && "max-w-[min(100vw-8rem,22rem)]",
            triggerSurface === "field" && "!bg-transparent hover:!bg-transparent dark:!bg-input/30 dark:hover:!bg-input/50",
            className
          )}
          style={
            triggerSurface === "field"
              ? { backgroundColor: "transparent", backgroundImage: "none" }
              : undefined
          }
          aria-label="Select date range"
          {...iconHoverHandlers}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <CalendarDaysIcon
              ref={iconRef}
              data-icon="inline-start"
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronDown
            data-icon="inline-end"
            className="size-4 shrink-0 opacity-80"
            aria-hidden
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="z-[2000] w-auto gap-0 p-0"
      >
        <Card
          data-date-range-picker
          data-comparison-mode={comparisonEnabled ? "true" : undefined}
          size="sm"
          className={cn(
            "mx-auto w-fit max-w-[min(100vw-24px,780px)] gap-0 rounded-[6px] border-0 py-0 shadow-none ring-0",
            comparisonEnabled && "max-w-[min(100vw-24px,680px)]",
            resolvedNumberOfMonths === 1 &&
              !comparisonEnabled &&
              "max-w-[min(100vw-24px,560px)]",
            resolvedNumberOfMonths === 1 &&
              comparisonEnabled &&
              "max-w-[min(100vw-24px,320px)]"
          )}
        >
          <CardContent className="flex flex-col gap-0 p-0">
            {comparisonEnabled ? (
              <div className="w-full border-b border-border px-3 py-2 text-center">
                <p
                  data-font="primary"
                  data-period-caption=""
                  className="text-sm font-medium normal-case tracking-normal text-[color:var(--brand-tropical-surf)]"
                >
                  Selected period
                </p>
              </div>
            ) : null}
            <Calendar
              key={`primary-${dateRange.startDate?.getTime() ?? "none"}-${dateRange.endDate?.getTime() ?? "none"}`}
              mode="range"
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              selected={dayPickerRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={resolvedNumberOfMonths}
              disabled={(date) =>
                isFutureCalendarDay(date) || date < (minDate ?? MIN_CALENDAR_DATE)
              }
              className="p-0 text-muted-foreground"
              classNames={PRIMARY_CALENDAR_CLASS_NAMES}
            />

            {supportsComparison ? (
              <div className="flex flex-col gap-0 border-t border-border">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <Label
                    htmlFor="date-range-compare-switch"
                    className="cursor-pointer text-sm font-medium normal-case text-muted-foreground"
                  >
                    Compare
                  </Label>
                  <Switch
                    id="date-range-compare-switch"
                    size="sm"
                    checked={comparisonEnabled}
                    onCheckedChange={handleComparisonToggle}
                    aria-label="Compare to another period"
                  />
                </div>

                {comparisonEnabled ? (
                  <>
                    <div className="w-full border-t border-border px-3 py-2 text-center">
                      <p
                        data-font="primary"
                        data-period-caption=""
                        data-comparison-caption=""
                        className="text-sm font-medium normal-case tracking-normal text-[color:var(--brand-comparison-period)]"
                      >
                        Comparison period
                      </p>
                    </div>
                    <Calendar
                      data-comparison-calendar
                      key={`comparison-${comparisonRange?.startDate?.getTime() ?? "none"}-${comparisonRange?.endDate?.getTime() ?? "none"}`}
                      mode="range"
                      month={comparisonMonth}
                      onMonthChange={setComparisonMonth}
                      selected={comparisonDayPickerRange}
                      onSelect={handleComparisonCalendarSelect}
                      numberOfMonths={resolvedNumberOfMonths}
                      pagedNavigation={resolvedNumberOfMonths > 1}
                      disabled={(date) =>
                        isFutureCalendarDay(date) || date < MIN_CALENDAR_DATE
                      }
                      className="p-0 text-muted-foreground"
                      classNames={COMPARISON_CALENDAR_CLASS_NAMES}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
          </CardContent>

          {showPresets || comparisonEnabled ? (
          <CardFooter
            data-date-range-presets=""
            className={cn(
              "border-t border-border bg-muted/50",
              comparisonEnabled
                ? "flex flex-col gap-2"
                : // Mobile: one preset column under the calendar. sm+: two columns.
                  "grid grid-cols-1 gap-2 sm:grid-cols-2"
            )}
          >
            {comparisonEnabled ? (
              COMPARISON_PRESET_OPTIONS.map((option) => (
                <Button
                  key={option.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  data-preset-chip="true"
                  data-comparison-preset="true"
                  data-active={
                    comparisonPreset === option.id ? "true" : undefined
                  }
                  disabled={!primaryRangeReady}
                  className="h-auto min-h-8 w-full whitespace-normal py-1.5 text-left text-muted-foreground"
                  onClick={() => handleComparisonPresetSelect(option.id)}
                >
                  {option.label}
                </Button>
              ))
            ) : (
              visibleDatePresetOptions.map((option) => (
                <Button
                  key={option.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  data-preset-chip="true"
                  data-active={activePresetId === option.id ? "true" : undefined}
                  className="w-full text-muted-foreground"
                  onClick={() => handlePresetSelect(option.id)}
                >
                  {option.label}
                </Button>
              ))
            )}
          </CardFooter>
          ) : null}
        </Card>
      </PopoverContent>
    </Popover>
  )
}

function parseYmdToDate(ymd: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return undefined

  const parsed = new Date(`${ymd}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return undefined

  return startOfLocalDay(parsed)
}

function formatDateToYmd(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

export interface SingleDatePickerProps {
  id?: string
  value: string
  onChange: (ymd: string) => void
  minDate?: string
  maxDate?: string
  placeholder?: string
  className?: string
}

export function SingleDatePicker({
  id,
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select date",
  className,
}: SingleDatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selected = React.useMemo(() => parseYmdToDate(value), [value])
  const min = minDate ? parseYmdToDate(minDate) : undefined
  const max = maxDate ? parseYmdToDate(maxDate) : startOfLocalDay(new Date())

  const [currentMonth, setCurrentMonth] = React.useState<Date>(() =>
    monthStart(selected ?? max ?? new Date())
  )

  React.useEffect(() => {
    if (selected) {
      setCurrentMonth(monthStart(selected))
    }
  }, [selected])

  const iconRef = React.useRef<CalendarDaysIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  const disabled = React.useCallback(
    (date: Date) => {
      const day = startOfLocalDay(date)
      if (max && day > max) return true
      if (min && day < min) return true
      return date < MIN_CALENDAR_DATE
    },
    [min, max]
  )

  const triggerLabel = selected
    ? format(selected, "MMM d, yyyy")
    : placeholder

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (nextOpen) {
        setCurrentMonth(monthStart(selected ?? max ?? new Date()))
      }
    },
    [selected, max]
  )

  const handleSelect = React.useCallback(
    (date: Date | undefined) => {
      if (!date) {
        onChange("")
        return
      }
      onChange(formatDateToYmd(date))
      setOpen(false)
    },
    [onChange]
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-date-range-trigger=""
          data-empty={!selected ? "true" : undefined}
          className={cn(
            "h-8 shrink-0 justify-between gap-1.5 font-normal normal-case tracking-normal",
            !selected && "text-muted-foreground",
            className
          )}
          aria-label={placeholder}
          {...iconHoverHandlers}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <CalendarDaysIcon
              ref={iconRef}
              data-icon="inline-start"
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronDown
            data-icon="inline-end"
            className="size-4 shrink-0 opacity-80"
            aria-hidden
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="z-[2000] w-auto gap-0 p-0"
      >
        <Card
          data-date-range-picker
          size="sm"
          className="mx-auto w-fit max-w-[min(100vw-24px,320px)] gap-0 rounded-[6px] border-0 py-0 shadow-none ring-0"
        >
          <CardContent className="flex flex-col gap-0 p-0">
            <Calendar
              key={value || "none"}
              mode="single"
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              selected={selected}
              onSelect={handleSelect}
              numberOfMonths={1}
              disabled={disabled}
              className="p-0 text-muted-foreground"
              classNames={SINGLE_CALENDAR_CLASS_NAMES}
            />
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  )
}
